import React, { useMemo } from 'react';
import { Table, Tag, Avatar, Empty, Select, Card, Statistic, Space } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined, 
  StarOutlined, 
  CalendarOutlined, 
  ExclamationCircleOutlined, 
  CarOutlined, 
  FileTextOutlined,
  UserOutlined
} from '@ant-design/icons';

import moment from 'moment-jalaali';
import AttendanceSummaryMetrics, { extractAttendanceSummary } from './AttendanceSummaryMetrics';
import AttendanceAdjustedTime from './AttendanceAdjustedTime';
import LateSummaryCard from './LateSummaryCard';
import { resolveIsLate, getAttendancePalette } from '../../../utils/attendanceStatusStyles';

const { Option } = Select;

const AttendanceByMemberView = ({
  data,
  loading = false,
  filters = {},
  onFiltersChange,
  employees = [],
}) => {
  const jYear = filters.year || moment().format('jYYYY');
  const jMonthName = filters.monthName || 'آذر';
  const userId = filters.memberUserId || filters.userId || filters.employee || undefined;

  const dates = data?.dates || {};
  const stats = data?.stats || {};
  const summary = extractAttendanceSummary(data) || data?.summary || null;

  const attendanceData = useMemo(() => {
    const rows = [];
    Object.entries(dates).forEach(([date, info]) => {
      const punch = info?.attendance?.[0] || null;
      const isLate = resolveIsLate(punch);
      const missionObj = info?.mission || null;
      const hasMission =
        Boolean(missionObj) ||
        punch?.status === 'mission' ||
        info?.status === 'mission' ||
        info?.mission_id != null;

      let status = info?.holiday
        ? 'holiday'
        : info?.leave
          ? 'leave'
          : hasMission
            ? 'mission'
            : info?.day_off
              ? 'dayOff'
              : punch?.status || (info?.attendance?.length ? 'present' : 'absent');
      if (isLate && (status === 'present' || !status)) {
        status = 'late';
      }
      // هرگز مأموریت را به‌عنوان غیبت نشان نده
      if (hasMission && status === 'absent') {
        status = 'mission';
      }
      rows.push({
        id: date,
        date,
        status,
        entry: punch?.clock_in_time
          ? moment(punch.clock_in_time).format('HH:mm')
          : punch?.clock_in_time_formatted || null,
        exit: punch?.clock_out_time
          ? moment(punch.clock_out_time).format('HH:mm')
          : punch?.clock_out_time_formatted || null,
        total: punch?.total_formatted || punch?.formatted || null,
        holidayTitle: info?.holiday?.occassion,
        attendanceId: punch?.id ?? null,
        isAdjusted: Boolean(punch?.is_adjusted),
        isLate,
        originalIn: punch?.original_clock_in_time_formatted || punch?.original_clock_in_time || null,
        originalOut: punch?.original_clock_out_time_formatted || punch?.original_clock_out_time || null,
        adjustments: punch?.adjustments || null,
        mission: missionObj,
        missionDestination: missionObj?.destination || info?.destination || null,
        missionTripType: missionObj?.trip_type || info?.trip_type || null,
      });
    });
    return rows;
  }, [dates]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'absent':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'late':
        return <ClockCircleOutlined style={{ color: '#92400e' }} />;
      case 'halfDay':
        return <ExclamationCircleOutlined style={{ color: '#c2410c' }} />;
      case 'holiday':
        return <CalendarOutlined style={{ color: '#64748b' }} />;
      case 'dayOff':
        return <CheckCircleOutlined style={{ color: '#64748b' }} />;
      case 'leave':
        return <FileTextOutlined style={{ color: '#1d4ed8' }} />;
      case 'mission': {
        const missionPalette = getAttendancePalette('mission');
        return <CarOutlined style={{ color: missionPalette?.color || '#3730a3' }} />;
      }
      case 'note':
        return <StarOutlined style={{ color: '#faad14' }} />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      present: 'حاضر',
      absent: 'غیبت',
      late: 'تأخیر',
      halfDay: 'نیم روز',
      holiday: 'تعطیلی',
      dayOff: 'روز تعطیل',
      leave: 'مرخصی',
      mission: 'مأموریت',
      note: 'یادداشت',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      present: 'green',
      absent: 'red',
      late: 'gold',
      halfDay: 'orange',
      holiday: 'default',
      dayOff: 'default',
      leave: 'blue',
      mission: 'purple',
      note: 'gold',
    };
    return colorMap[status] || 'default';
  };

  const columns = [
    {
      title: 'تاریخ',
      dataIndex: 'date',
      key: 'date',
      render: (text, record) => (
        <div style={{ fontFamily: 'CustomFont' }}>{moment(text).format('jYYYY/jMM/jDD')}</div>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const latePalette = getAttendancePalette('late');
        const missionPalette = getAttendancePalette('mission');
        const isMission = status === 'mission';
        return (
          <div className="inline-flex flex-col items-start gap-1">
            <Tag
              color={isMission ? undefined : getStatusColor(status)}
              icon={getStatusIcon(status)}
              style={
                isMission
                  ? {
                      background: missionPalette.bg,
                      color: missionPalette.color,
                      borderColor: missionPalette.border,
                    }
                  : undefined
              }
            >
              {getStatusText(status)}
            </Tag>
            {isMission && record.missionTripType ? (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded border font-medium"
                style={{
                  background: missionPalette.bg,
                  color: missionPalette.color,
                  borderColor: missionPalette.border,
                  fontFamily: 'CustomFont',
                }}
              >
                {record.missionTripType === 'outside_city' ? 'برون‌شهری' : 'درون‌شهری'}
              </span>
            ) : null}
            {record.isLate && !isMission ? (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full border font-semibold"
                style={{
                  background: latePalette.bg,
                  color: latePalette.color,
                  borderColor: latePalette.border,
                  fontFamily: 'CustomFont',
                }}
              >
                تأخیر
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'ورود',
      dataIndex: 'entry',
      key: 'entry',
      render: (text, record) => (
        <div className="inline-flex items-center gap-1.5">
          <AttendanceAdjustedTime
            time={text}
            isAdjusted={record.isAdjusted}
            attendanceId={record.attendanceId}
            field="clock_in"
            originalFormatted={record.originalIn}
            adjustments={record.adjustments}
          />
          {record.isLate && text ? (
            <Tag color="gold" className="custom-font m-0 text-[10px] px-1 leading-none">
              تأخیر
            </Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: 'خروج',
      dataIndex: 'exit',
      key: 'exit',
      render: (text, record) => (
        <AttendanceAdjustedTime
          time={text}
          isAdjusted={record.isAdjusted}
          attendanceId={record.attendanceId}
          field="clock_out"
          originalFormatted={record.originalOut}
          adjustments={record.adjustments}
        />
      ),
    },
    {
      title: 'مجموع',
      dataIndex: 'total',
      key: 'total',
      render: (text) => text || '-',
    },
    {
      title: 'سایر',
      dataIndex: 'other',
      key: 'other',
      render: (_, record) => {
        if (record.status === 'mission') {
          return (
            <span
              className="text-xs text-indigo-800"
              style={{ fontFamily: 'CustomFont' }}
              title={record.missionDestination || ''}
            >
              {record.missionDestination || 'مأموریت کاری'}
            </span>
          );
        }
        if (record.holidayTitle) return record.holidayTitle;
        return '-';
      },
    },
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      {/* فیلترها */}
      <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
        <Space wrap className="w-full" size="middle">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">سال:</span>
            <Select
              value={jYear}
              onChange={(v) => onFiltersChange?.({ year: v })}
              style={{ width: 120, fontFamily: 'CustomFont' }}
            >
              <Option value="1404">۱۴۰۴</Option>
              <Option value="1403">۱۴۰۳</Option>
              <Option value="1402">۱۴۰۲</Option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">ماه:</span>
            <Select
              value={jMonthName}
              onChange={(v) => onFiltersChange?.({ monthName: v })}
              style={{ width: 140, fontFamily: 'CustomFont' }}
            >
              <Option value="فروردین">فروردین</Option>
              <Option value="اردیبهشت">اردیبهشت</Option>
              <Option value="خرداد">خرداد</Option>
              <Option value="تیر">تیر</Option>
              <Option value="مرداد">مرداد</Option>
              <Option value="شهریور">شهریور</Option>
              <Option value="مهر">مهر</Option>
              <Option value="آبان">آبان</Option>
              <Option value="آذر">آذر</Option>
              <Option value="دی">دی</Option>
              <Option value="بهمن">بهمن</Option>
              <Option value="اسفند">اسفند</Option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">کارمند:</span>
            <Select
              value={userId}
              onChange={(v) => onFiltersChange?.({ memberUserId: v, userId: v, employee: v })}
              style={{ width: 220, fontFamily: 'CustomFont' }}
              showSearch
              optionFilterProp="children"
              placeholder="انتخاب کارمند"
            >
              {employees.map((e) => (
                <Option key={e.id} value={String(e.id)}>
                  <div className="flex items-center gap-2">
                    <Avatar size="small" icon={<UserOutlined />} src={e.avatar} />
                    <span>{e.name}</span>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        </Space>
      </Card>

      {/* شاخص‌های ۶گانه ساعتی */}
      <AttendanceSummaryMetrics summary={summary} />

      <LateSummaryCard
        lateTime={summary?.late_time}
        lateMinutes={summary?.late_minutes}
        lateDaysCount={stats.late || 0}
      />

      {/* کارت‌های خلاصه روزها */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6">
        <Card className="bg-white dark:bg-gray-800">
          <Statistic
            title={<span style={{ fontFamily: 'CustomFont' }}>تعطیلات</span>}
            value={stats.holidays || 0}
            prefix={<CalendarOutlined />}
            valueStyle={{ fontFamily: 'CustomFont' }}
          />
        </Card>
        <Card className="bg-white dark:bg-gray-800">
          <Statistic
            title={<span style={{ fontFamily: 'CustomFont' }}>غیبت</span>}
            value={stats.absent || stats.absence || 0}
            prefix={<CloseCircleOutlined />}
            valueStyle={{ fontFamily: 'CustomFont', color: '#be123c' }}
          />
        </Card>
        <Card
          className="bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800"
        >
          <Statistic
            title={<span style={{ fontFamily: 'CustomFont', color: '#3730a3' }}>مأموریت</span>}
            value={stats.missions || stats.mission || 0}
            prefix={<CarOutlined style={{ color: '#4338ca' }} />}
            valueStyle={{ fontFamily: 'CustomFont', color: '#3730a3', fontWeight: 700 }}
          />
        </Card>
        <Card className="bg-white dark:bg-gray-800">
          <Statistic
            title={<span style={{ fontFamily: 'CustomFont' }}>نیم روز</span>}
            value={stats.half_day || 0}
            prefix={<ExclamationCircleOutlined />}
            valueStyle={{ fontFamily: 'CustomFont', color: '#c2410c' }}
          />
        </Card>
        <Card
          className="bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
        >
          <Statistic
            title={<span style={{ fontFamily: 'CustomFont', color: '#92400e' }}>تأخیر (روز)</span>}
            value={stats.late || 0}
            prefix={<ClockCircleOutlined style={{ color: '#d97706' }} />}
            valueStyle={{ fontFamily: 'CustomFont', color: '#92400e', fontWeight: 700 }}
          />
        </Card>
        <Card className="bg-white dark:bg-gray-800">
          <Statistic
            title={<span style={{ fontFamily: 'CustomFont' }}>روزهای حضور</span>}
            value={stats.present || stats.attendanceDays || 0}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ fontFamily: 'CustomFont' }}
          />
        </Card>
        <Card className="bg-white dark:bg-gray-800">
          <Statistic
            title={<span style={{ fontFamily: 'CustomFont' }}>روزهای کاری</span>}
            value={stats.totalWorkingDays || stats.workDays || 0}
            prefix={<CalendarOutlined />}
            valueStyle={{ fontFamily: 'CustomFont' }}
          />
        </Card>
      </div>

      {/* جدول */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <Table
          columns={columns}
          dataSource={attendanceData}
          rowKey="id"
          pagination={{
            pageSize: filters.pageSize || 30,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100'],
            showTotal: (total, range) => `نمایش ${range[0]} تا ${range[1]} از ${total} رکورد`,
            onShowSizeChange: (_, size) => onFiltersChange?.({ pageSize: size }),
          }}
          loading={loading}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                description={<span style={{ fontFamily: 'CustomFont' }}>هیچ رکوردی یافت نشد</span>}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          style={{ fontFamily: 'CustomFont' }}
        />
      </div>
    </div>
  );
};

export default AttendanceByMemberView;
